import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useGetUserAndAccountSettings } from '@/lib/api/_hooks';
import { withLaunchDarkly, type LaunchDarklyFlags } from '../common/launchDarkly';
import NavHeader from '../common/navigation/NavHeader';
import { DocumentPreview } from '../components/applyTemplate/DocumentPreview';
import { Documents } from '../components/applyTemplate/Documents';
import { Footer } from '../components/applyTemplate/Footer';
import { TemplatesDrawer } from '../components/applyTemplate/templates-drawer';
import { HeaderContext } from '../context/header-context';
import { getUsersGroups } from '../lib/api/account/accountApi';
import { chameleonElId } from '../lib/constants';
import { preferencesUrl } from '../lib/environment-config';
import { useDispatch, useShallowSelector } from '../lib/reduxHooks';
import { UserOrigins } from '../lib/types';
import * as actions from '../store/applyTemplate/actions';
import { setLoading } from '../store/pageFrame/actions';

interface AppliedTemplate {
  template: string;
  data: {
    document: string;
  };
}

function ApplyTemplate({ flags }: { flags: LaunchDarklyFlags }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams<{ id: string }>();

  const isTemplateSharingEnabled = flags?.['enable-template-sharing'];
  const hideNavItems = flags?.['hide-build-ui-elements'];
  const showPreferencesMenuItem = flags?.['add-automatic-time-stamp-signatures-to-user-preferences'];

  const store = useShallowSelector((state) => ({
    envelope: state.applyTemplate.envelope,
    envelopeFetched: !!state.applyTemplate.envelope,
    templatesFetched: !!state.applyTemplate.templates,
    appliedTemplates: state.applyTemplate.appliedTemplates,
    fetchedAppliedTemplates: state.applyTemplate.fetchedAppliedTemplates,
    showTemplatesSection: state.applyTemplate.showTemplatesSection,
    documents: state.applyTemplate.envelope?.documents,
    userOrigin: state.pageFrame.settings?.account.userOrigin,
    templateApplied: state.applyTemplate.templateApplied,
    loading: state.pageFrame.loading,
  }));

  const settings = useGetUserAndAccountSettings();

  const [templatesList, setTemplatesList] = React.useState<AppliedTemplate[]>([]);
  const headerContext = React.useContext(HeaderContext);

  React.useEffect(() => {
    if (!store.envelopeFetched) {
      dispatch(actions.fetchEnvelope(params.id));
    }
    return () => {
      dispatch(actions.reset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, []);

  React.useEffect(() => {
    if (!settings.isFetched || store.templatesFetched) return;

    const urlParams = new URLSearchParams(window.location.search);
    const stateQueryParam = urlParams.get('state');
    const roleQueryParam = urlParams.get('role');
    const flashCodes = urlParams.get('flash');
    const flashCodesList = flashCodes ? flashCodes.split(',') : [];

    const templateQueryParams =
      stateQueryParam && roleQueryParam
        ? { state: stateQueryParam, flashCodes: flashCodesList, role: roleQueryParam }
        : undefined;

    dispatch(actions.fetchTemplates({ templateQueryParams, fetchShares: isTemplateSharingEnabled }));
  }, [dispatch, isTemplateSharingEnabled, settings.isFetched, store.templatesFetched]);

  React.useEffect(() => {
    if (!settings.isFetched) return;

    headerContext.setContent(
      <NavHeader
        labelName="Apply templates"
        breadcrumb={hideNavItems ? undefined : { name: 'Envelopes', onClick: () => history.push('/envelopes') }}
        hideNavItems={hideNavItems}
        preferencesUrl={preferencesUrl}
        showPreferences={showPreferencesMenuItem}
        getUsersGroups={getUsersGroups}
        flags={flags}
      />
    );
    return () => headerContext.setContent(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.isFetched]);

  React.useEffect(() => {
    if (store.templatesFetched && !store.fetchedAppliedTemplates) {
      dispatch(actions.fetchTemplatesApplied(params.id, true));
    }
  }, [store.templatesFetched, store.fetchedAppliedTemplates, dispatch, params.id]);

  React.useEffect(() => {
    if (store.templateApplied) {
      if (store.envelope?.config?.senderUi?.envelopePathPrefix === 'dtm/') {
        history.push(`/dtm/envelopes/${params.id}/recipients`);
      } else if (store.userOrigin === UserOrigins.DOP) {
        history.push(`/envelopes/${params.id}`);
      } else {
        history.push(`/envelopes/${params.id}/recipients`);
      }
    }
    dispatch(setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [store.templateApplied]);

  React.useEffect(() => {
    if (!store.fetchedAppliedTemplates) return;
    for (const docId in store.appliedTemplates) {
      const templateId = store.appliedTemplates[docId]?.metadata?.id;
      if (templateId) {
        templatesList.push({ data: { document: docId }, template: templateId });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [store.fetchedAppliedTemplates]);

  async function handleApplyTemplate() {
    dispatch(setLoading(true));
    const bulkTemplates = templatesList.map((t) => ({ envelopeDocumentId: t.data.document, templateId: t.template }));
    const filteredTemplates = bulkTemplates.filter((t) =>
      store.documents?.some((d) => t.envelopeDocumentId === d.documentId)
    );
    dispatch(actions.finalizeBulkTemplates(params.id, filteredTemplates));
  }

  function handleAddTemplate(template: AppliedTemplate, documentId?: string) {
    const templateListCopy = templatesList.filter((t) => t.data.document !== documentId);
    templateListCopy.push(template);
    setTemplatesList(templateListCopy);
  }

  function handleRemoveTemplate(documentId: string) {
    const templateListCopy: Array<AppliedTemplate> = [...templatesList];
    templateListCopy.forEach((template, i) => {
      if (template.data.document === documentId) {
        templateListCopy.splice(i, 1);
      }
    });
    setTemplatesList(templateListCopy);
  }

  if (!store.envelopeFetched) return null;

  return (
    <div className="ApplyTemplate grow relative -mt-6 flex" style={{ maxHeight: 'calc(100vh - 170px)' }}>
      <Documents
        id={chameleonElId(23)}
        className="border-r border-grey-400 basis-[367px] overflow-auto max-xl:hidden"
      />

      <DocumentPreview id={chameleonElId(24)} className="bg-white flex-1 w-0 overflow-auto" />

      {store.showTemplatesSection && (
        <TemplatesDrawer
          handleAddTemplate={handleAddTemplate}
          handleRemoveTemplate={handleRemoveTemplate}
          id={chameleonElId(25)}
          className="border-l border-grey-400 basis-[30%] lg:basis-[367px] max-w-[367px] grow-0 overflow-auto"
        />
      )}

      <Footer applyTemplate={handleApplyTemplate} flags={flags} loading={store.loading} />
    </div>
  );
}

const withLd = withLaunchDarkly(ApplyTemplate);
export { withLd as ApplyTemplate };
